import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { defineFillAction } from '@/shared/form/agent-fill-action';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { CREDIT_NOTE_LIST_PATH } from './credit-note-list.screen';

// Manifesto da Nova operação e as ações do formulário da operação (U23/U24). Arquivo leve: entra no
// `src/app/screens.ts`; o fio entra por import dinâmico. Simular não vira ferramenta: só roda por ação
// humana (o legado chama a simulação a partir do botão).

export const CREDIT_NOTE_NEW_PATH = `${CREDIT_NOTE_LIST_PATH}/nova`;

/** As abas da operação nova, na ordem do legado (CreditNoteForm.tsx:76-88, sem as de operação existente). */
export const CREDIT_NOTE_NEW_TABS = [
  { id: 'informacoes', label: msg`Informações` },
  { id: 'garantias', label: msg`Garantias` },
  { id: 'envolvidos', label: msg`Envolvidos` },
  { id: 'operacao', label: msg`Operação` },
  { id: 'documentos', label: msg`Documentos` },
] as const;
export type CreditNoteNewTab = (typeof CREDIT_NOTE_NEW_TABS)[number]['id'];
const NEW_TAB_IDS = CREDIT_NOTE_NEW_TABS.map((tab) => tab.id) as [CreditNoteNewTab, ...CreditNoteNewTab[]];

const id = z.string().trim().min(1).max(64).regex(/^[\w-]+$/);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const WARRANTY_PARAM = 'warranty=';

/**
 * O link antigo da Simulação (SimulationForm.tsx:69): `?simulacao={id}?warranty=[…]`, com dois `?`. A busca lê o
 * `simulacao` inteiro (`{id}?warranty=[…]`); aqui ele vira os dois parâmetros, como o formato novo. O legado pegava o JSON
 * depois do `warranty=` (CreditNoteForm.tsx:47-55); JSON inválido quebrava (`JSON.parse`): aqui fica texto, sem garantia
 * e sem query (`simulationWarranty`). R5.
 */
export function legacySimulationLink(raw: unknown): unknown {
  if (!isRecord(raw) || typeof raw['simulacao'] !== 'string') return raw;
  const value = raw['simulacao'];
  const at = value.indexOf('?');
  if (at < 0) return raw;
  const rest = value.slice(at + 1);
  const next: Record<string, unknown> = { ...raw, simulacao: value.slice(0, at) };
  if (rest.startsWith(WARRANTY_PARAM) && next['warranty'] === undefined) {
    const json = rest.slice(WARRANTY_PARAM.length);
    try {
      next['warranty'] = JSON.parse(json) as unknown;
    } catch {
      next['warranty'] = json;
    }
  }
  return next;
}

/**
 * Busca da Nova: a aba, `simulacao` (a simulação recuperada ao abrir, `nova?simulacao=`, CreditNoteForm.tsx:41-68)
 * e `duplicar` ("Duplicar como novo" abre a Nova com a cópia; no legado ia no `state` da navegação). O link antigo
 * com dois `?` também vale (`legacySimulationLink`).
 */
const creditNoteNewSearchObject = z.object({
  tab: z.enum(NEW_TAB_IDS).optional().catch(undefined),
  simulacao: id.optional().catch(undefined),
  /**
   * As garantias que a Simulação manda junto (SimulationForm.tsx:66-72 → CreditNoteForm.tsx:47-55, `warranty=` em JSON). O
   * legado montava `?simulacao={id}?warranty=[…]`; aqui vai como parâmetro próprio da busca, sem limite, e
   * `simulationWarranty` aplica como o legado.
   */
  warranty: z.unknown().optional(),
  duplicar: id.optional().catch(undefined),
});
export const creditNoteNewSearch = z.preprocess(legacySimulationLink, creditNoteNewSearchObject);
export type CreditNoteNewSearch = z.infer<typeof creditNoteNewSearchObject>;

const isPlainObject = isRecord;

/**
 * O `warranty=` da Nova como o legado o usa (CreditNoteForm.tsx:43-55): a lista só vale se for um array não vazio (senão,
 * `[]`); e, como o `simulationId` do legado é tudo depois do primeiro `=`, o JSON segue na query do `GET /Amortization/{id}`
 * (RecoverSimulationForm.tsx:40 → creditNote.ts:482-485). JSON inválido quebrava o legado (`JSON.parse`); aqui fica sem
 * garantia e sem query. Item que não é objeto sai da lista (o legado o gravaria no formulário).
 */
export function simulationWarranty(raw: unknown): { readonly list: Record<string, unknown>[]; readonly query: string | undefined } {
  if (raw === undefined || typeof raw === 'string') return { list: [], query: undefined };
  return { list: Array.isArray(raw) && raw.length > 0 ? raw.filter(isPlainObject) : [], query: JSON.stringify(raw) };
}

const civil = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** O que o agente pode preencher na aba Operação (os valores no formato do fio: centavos, fração, dia civil). */
/* eslint-disable lingui/no-unlocalized-strings -- valores do fio e descrições do agente em inglês (U33) */
export const operationChangesInput = z
  .object({
    calculateByValueType: z.enum(['Gross', 'Liquid', 'Payment']).optional(),
    requestedAmount: z.number().int().positive().optional().describe('Requested amount (or installment value when calculateByValueType is Payment) in integer cents'),
    apr: z.number().min(0).max(1).optional().describe('Interest rate as a fraction: 0.0199 is 1.99% a month'),
    numberOfPayments: z.number().int().positive().max(1000).optional().describe('Number of installments (price and sac)'),
    termInMonths: z.number().int().positive().max(1000).optional().describe('Duration in months (clean price and the other types)'),
    startDate: civil.optional().describe('Start date, civil YYYY-MM-DD'),
    firstPaymentDate: civil.optional().describe('First payment date, civil YYYY-MM-DD (price and sac)'),
  })
  .strict();
/* eslint-enable lingui/no-unlocalized-strings */
export type OperationChanges = z.infer<typeof operationChangesInput>;

const CHANGE_LABELS: Readonly<Record<keyof OperationChanges, MessageDescriptor>> = {
  calculateByValueType: msg`Tipo de cálculo`,
  requestedAmount: msg`Valor solicitado`,
  apr: msg`Taxa de juros`,
  numberOfPayments: msg`Quantidade de parcelas`,
  termInMonths: msg`Duração`,
  startDate: msg`Data de início`,
  firstPaymentDate: msg`Data do primeiro pagamento`,
};

/** Os campos que a ferramenta muda, com o nome da tela. */
export function changedFieldNames(changes: OperationChanges | undefined): string {
  return Object.keys(changes ?? {})
    .map((key) => i18n._(CHANGE_LABELS[key as keyof OperationChanges]))
    .join(', ');
}

/* eslint-disable lingui/no-unlocalized-strings -- respostas ao modelo, inglês canônico (U33) */
const UNBOUND = 'This tool only runs on the credit operation form open on screen (the detail or /ccb/operacoes/nova). Nothing was changed.';
/* eslint-enable lingui/no-unlocalized-strings */
const unbound = () => {
  throw new Error(UNBOUND);
};

/** O Salvar do agente: só os campos da aba Operação a preencher antes; o alvo é o formulário aberto (sem `id`). */
export const saveInput = z.strictObject({ changes: operationChangesInput.optional() });
export type SaveInput = z.infer<typeof saveInput>;

/** Prévia do Salvar (U24): a operação aberta (a tela troca pelo número) e os campos que a ferramenta muda. */
export function savePreview(input: SaveInput, record?: string) {
  const fields = changedFieldNames(input.changes);
  return {
    title: record ? i18n._(msg`Salvar a operação ${record}?`) : i18n._(msg`Salvar a operação nova?`),
    detail: fields ? i18n._(msg`Campos alterados: ${fields}. O Salvar valida o formulário inteiro, como na tela.`) : i18n._(msg`Salva o formulário como está na tela, sem alterar campos.`),
  };
}

export const creditNoteSave = action<SaveInput>({
  id: 'creditNote.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Salva o formulário da operação aberta na tela como o botão Salvar, com a mesma validação e o mesmo aviso. changes, opcional, preenche antes campos da aba Operação. Na Nova operação, cria a operação em Rascunho e a abre. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'Update'),
  input: saveInput,
  preview: (input) => savePreview(input),
  run: unbound,
});

export const recoverSimulationInput = z.strictObject({ simulationId: id });

export const creditNoteRecoverSimulation = action<z.infer<typeof recoverSimulationInput>>({
  id: 'creditNote.recoverSimulation',
  kind: 'draft',
  label: msg`Recuperar simulação`,
  activity: { running: msg`Recuperando a simulação`, done: msg`Simulação recuperada` },
  description: msg`Preenche o formulário da operação aberta com uma simulação salva (simulationId): amortização, produto, tomador e o resultado. Nada é salvo até o Salvar. Fora do formulário, abre uma Nova operação com a simulação.`,
  input: recoverSimulationInput,
  run: ({ input, nav }) => nav.to(CREDIT_NOTE_NEW_PATH, { search: { simulacao: input.simulationId } }),
});

/** Duplicar e Duplicar como novo agem sobre a operação aberta: sem alvo na entrada (U24). */
export const cloneInput = z.strictObject({});

export const creditNoteClone = action({
  id: 'creditNote.clone',
  kind: 'write',
  label: msg`Duplicar`,
  description: msg`Cria uma cópia da operação aberta na tela, com os dados lidos agora e sem número, status, parcelas e documentos, e abre a cópia. Pede a confirmação da pessoa.`,
  permission: can('CreditNote', 'Clone'),
  input: cloneInput,
  preview: () => ({ title: i18n._(msg`Duplicar a operação?`), detail: i18n._(msg`Uma operação nova é criada com os dados desta e aberta em seguida.`) }),
  run: unbound,
});

export const creditNoteCloneAsNew = action({
  id: 'creditNote.cloneAsNew',
  kind: 'navigate',
  label: msg`Duplicar como novo`,
  description: msg`Abre a Nova operação preenchida com uma cópia da operação aberta na tela, em Rascunho. Nada é salvo até o Salvar.`,
  permission: can('CreditNote', 'Clone'),
  input: cloneInput,
  run: unbound,
});

// ─── o Luy na Nova operação (U56, L3): buscas e preenchimento, nunca Salvar, Simular nem consulta paga (X01) ──

const loadFind = () => import('./credit-note.find');

/* eslint-disable lingui/no-unlocalized-strings -- valores do fio e descrições do agente em inglês (U33) */
const recordId = id.describe('Id returned by the matching find action');
const cents = z.number().int().min(0);
const count = z.number().int().min(0).max(1000);
export const findInput = z.strictObject({
  query: z.string().trim().min(1).max(100).describe('Text to search: a name, or for a person also the CPF/CNPJ without mask'),
});
export type FindInput = z.infer<typeof findInput>;

export const creditNoteFindProduct = action<FindInput>({
  id: 'creditNote.findProduct',
  kind: 'read',
  label: msg`Buscar produto`,
  activity: { running: msg`Buscando produto`, done: msg`Produto encontrado`, empty: msg`Nenhum produto encontrado` },
  description: msg`Busca produtos pelo nome, como o seletor Produto da aba Informações. Devolve até 10 itens com id e rótulo; o id vai no productId do creditNote.fill. Não muda nada na tela.`,
  input: findInput,
  run: async ({ input }) => (await loadFind()).findProducts(input.query),
});

export const creditNoteFindFund = action<FindInput>({
  id: 'creditNote.findFund',
  kind: 'read',
  label: msg`Buscar cessionário`,
  activity: { running: msg`Buscando cessionário`, done: msg`Cessionário encontrado`, empty: msg`Nenhum cessionário encontrado` },
  description: msg`Busca cessionários pelo nome, como o seletor Cessionário da aba Informações. Devolve até 10 itens com id e rótulo; o id vai no fundId do creditNote.fill. Não muda nada na tela.`,
  input: findInput,
  run: async ({ input }) => (await loadFind()).findFunds(input.query),
});

export const creditNoteFindPerson = action<FindInput>({
  id: 'creditNote.findPerson',
  kind: 'read',
  label: msg`Buscar tomador`,
  activity: { running: msg`Buscando tomador`, done: msg`Tomador encontrado`, empty: msg`Nenhum tomador encontrado` },
  description: msg`Busca pessoas pelo nome ou pelo CPF/CNPJ sem máscara, como o seletor Tomador da aba Informações. Devolve até 10 itens com id, rótulo e documento; o id vai no personId (ou beneficiaryId) do creditNote.fill. Não muda nada na tela.`,
  input: findInput,
  run: async ({ input }) => (await loadFind()).findPersons(input.query),
});

const OPERATION = operationChangesInput.shape;

/** O que o Luy preenche na Nova operação, nos caminhos do formulário e no formato do fio (centavos, fração, dia civil). */
export const creditNoteFill = defineFillAction({
  id: 'creditNote.fill',
  description: msg`Preenche, como rascunho, as abas Informações e Operação da Nova operação aberta. Produto, cessionário, tomador e beneficiário vão pelo id de creditNote.findProduct, creditNote.findFund e creditNote.findPerson. O produto entra primeiro e traz os padrões dele (taxa, prazo, cessionário); depois entram os demais campos. Valores em centavos, taxa como fração (0.0199 = 1,99% ao mês) e datas no formato AAAA-MM-DD; a data de início recalcula a do primeiro pagamento, como na tela. Campo escondido ou desabilitado para o produto e campo que a pessoa já editou voltam em skipped. Nunca salva, simula nem faz consulta paga: quem salva é a pessoa, no botão Salvar.`,
  mountedMessage: msg`Abra a Nova operação para o Luy preencher.`,
  activity: { running: msg`Preenchendo a operação`, done: msg`Operação preenchida` },
  fields: {
    productId: { schema: recordId, label: msg`Produto` },
    fundId: { schema: recordId, label: msg`Cessionário` },
    personId: { schema: recordId, label: msg`Tomador` },
    beneficiaryId: { schema: recordId.describe('Person id from creditNote.findPerson; only for consumer credit (CDC) outside the liquidation schedule'), label: msg`Beneficiário` },
    liquidationMode: { schema: z.enum(['Simple', 'LiquidationSchedule']), label: msg`Modo de liquidação` },
    liquidationType: { schema: z.string().trim().min(1).max(64).describe('Invoice (boleto) or EletronicTransfer (TED or PIX), unless the tenant configures other options'), label: msg`Tipo de liquidação` },
    bankAccountId: { schema: id.describe('Bank account id of the borrower (of the beneficiary in CDC); listed in the screen context as bankAccounts'), label: msg`Conta de liquidação` },
    invoiceBarCode: { schema: z.string().regex(/^\d{44,48}$/).describe('Boleto bar code, digits only'), label: msg`Código de Barras` },
    invoiceNumber: { schema: z.string().trim().min(1).max(60), label: msg`Nº da NF/Contrato` },
    invoiceValue: { schema: cents.describe('Invoice value in integer cents'), label: msg`Valor da NF/Contrato` },
    isRegistered: { schema: z.boolean(), label: msg`Registradora de recebíveis` },
    bulkAssignment: { schema: z.boolean(), label: msg`Cessão em lote` },
    observations: { schema: z.string().trim().max(2000), label: msg`Observações adicionais` },
    'amortization.calculateByValueType': { schema: OPERATION.calculateByValueType, label: msg`Tipo de cálculo` },
    'amortization.requestedAmount': { schema: OPERATION.requestedAmount, label: msg`Valor solicitado` },
    'amortization.apr': { schema: OPERATION.apr, label: msg`Taxa de juros` },
    'amortization.numberOfPayments': { schema: OPERATION.numberOfPayments, label: msg`Quantidade de parcelas` },
    'amortization.termInMonths': { schema: OPERATION.termInMonths, label: msg`Duração` },
    'amortization.numberOfInterestPayments': { schema: count.describe('Principal grace period, in installments (price and sac)'), label: msg`Carência de principal` },
    'amortization.absAmortizationInMonths': { schema: count.describe('Amortization grace period in months (clean price and the other types)'), label: msg`Carência amortização` },
    'amortization.absInterestInMonths': { schema: count.describe('Interest grace period in months (clean price and the other types)'), label: msg`Carência juros` },
    'amortization.paymentPeriodicity.every': { schema: z.number().int().min(1).max(1000).describe('Pay every N periods (price and sac)'), label: msg`Pagamento à cada` },
    'amortization.paymentPeriodicity.periodicity': { schema: z.enum(['Daily', 'Monthly', 'Yearly']).describe('Payment period unit (price and sac)'), label: msg`Periodicidade do pagamento` },
    'amortization.periodicity': { schema: z.enum(['Monthly', 'Yearly']).describe('Interest periodicity (clean price and the other types)'), label: msg`Periodicidade juros` },
    'amortization.startDate': { schema: OPERATION.startDate, label: msg`Data de início` },
    'amortization.paymentDay': { schema: z.number().int().min(1).max(31).describe('Due day of the month, when the tenant shows it'), label: msg`Dia de vencimento` },
    'amortization.firstPaymentDate': { schema: OPERATION.firstPaymentDate, label: msg`Data do primeiro pagamento` },
  },
});
/* eslint-enable lingui/no-unlocalized-strings */

export const creditNoteNewScreen = defineScreen({
  id: 'creditNote.new',
  path: CREDIT_NOTE_NEW_PATH,
  title: msg`Nova operação`,
  description: msg`Formulário de Nova operação: produto, tomador e liquidação (Informações), parâmetros da operação com simulação e plano de pagamento (Operação), garantias, envolvidos e documentos. Salvar cria a operação em Rascunho.`,
  // No legado a Nova é a rota `/ccb/operacoes/:id` com `id = nova`, sob `/ccb` (`CreditNote:Read`, router.tsx:994-1011);
  // criar exige `CreditNote:Create` no Salvar.
  permission: can('CreditNote', 'Read'),
  place: { icon: 'receipt-text', parent: 'creditNote.list', tabs: CREDIT_NOTE_NEW_TABS },
  actions: [{ ...creditNoteSave, permission: can('CreditNote', 'Create') }, creditNoteRecoverSimulation, creditNoteFill, creditNoteFindProduct, creditNoteFindFund, creditNoteFindPerson],
});

/** O que o Luy pode preencher agora (U56): os campos livres e, dos outros, o motivo. */
export interface CreditNoteFillState {
  readonly available: readonly string[];
  readonly unavailable: Readonly<Record<string, string>>;
}

/**
 * O que o agente lê da Nova operação (U24, U46 D2-C, U56), no idioma da tela: aba, status, produto, os valores das abas
 * Informações e Operação (inclusive o CPF/CNPJ do tomador: o Luy recebe sem máscara, L07/L09; agente de fora recebe só
 * o resumo mascarado, `restricted`) e o que o Luy pode preencher agora.
 */
export function creditNoteNewContext(input: {
  readonly tab: string;
  readonly product: string | undefined;
  readonly dirty: boolean;
  readonly readOnly: boolean;
  readonly actions: readonly string[];
  readonly status?: string;
  readonly values?: Readonly<Record<string, unknown>>;
  readonly fill?: CreditNoteFillState;
  readonly bankAccounts?: readonly { readonly id: string; readonly label: string }[];
}): ScreenContext {
  const found = CREDIT_NOTE_NEW_TABS.find((item) => item.id === input.tab);
  const tab = found ? i18n._(found.label) : input.tab;
  const product = input.product;
  const actions = input.actions.join(', ');
  const fillable = input.fill?.available.length ?? 0;
  return {
    summary: [
      t`Nova operação, aba ${tab}.`,
      product ? t`Produto ${product}.` : t`Produto ainda não escolhido.`,
      input.dirty ? t`Há alterações não salvas.` : '',
      input.actions.length > 0 ? t`Ações disponíveis: ${actions}.` : '',
      input.fill ? i18n._(msg({ message: plural(fillable, { one: 'O Luy pode preencher # campo agora; quem salva é a pessoa.', other: 'O Luy pode preencher # campos agora; quem salva é a pessoa.' }) })) : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: {
      tab: input.tab,
      product: input.product ?? null,
      dirty: input.dirty,
      readOnly: input.readOnly,
      ...(input.status === undefined ? {} : { status: input.status }),
      ...(input.values ? { values: input.values } : {}),
      ...(input.bankAccounts ? { bankAccounts: input.bankAccounts } : {}),
      ...(input.fill ? { fillable: input.fill.available, notFillable: input.fill.unavailable } : {}),
    },
    sensitivity: 'restricted',
  };
}
